import { alpha } from '@mui/material/styles'

import { border as borderToken } from 'settings/tokens'

/**
 * A FIELD's resting border — deliberately darker than the app's `border.subtle`
 * hairline, and the one value that decides whether a field reads as a container.
 *
 * ## Why this is not `border.subtle` (VRG, 2026-09-02)
 *
 * It was, and the result was a field with no visible container. MEASURED on
 * `/sign-in` at 375x812 before this change:
 *
 *   | pair                                    | ratio  |
 *   |-----------------------------------------|--------|
 *   | fill `grey[50]` #F4F5F5 vs white page    | 1.09:1 |
 *   | border `border.subtle` #E4E4E7 vs page   | 1.27:1 |
 *   | placeholder #7A7A7A vs fill              | 3.93:1 |
 *
 * Neither the fill nor the border was perceivable, so the only thing marking
 * where the field was, was its PLACEHOLDER — and that sat under 4.5:1 too. A
 * field you locate by its placeholder is a field that vanishes the moment it has
 * a value.
 *
 * Note the fill cannot be made to carry this. On the auth pages the ground is
 * white (`AuthBackdrop`'s `BASE_LIGHT`) and on every other page it is `#F7F7F8`
 * (`palette.background.default`) — a light fill is within ~1.1:1 of BOTH, and
 * darkening it enough to separate from one drives it toward the other. The
 * BORDER is the only lever that works on both grounds, which is why the value
 * lives here rather than in the fill.
 *
 * ## The value
 *
 * #86868B gives **3.53:1 against white** and 3.42:1 against `#F7F7F8`, clearing
 * WCAG 2.2 1.4.11 Non-text Contrast (3:1), which applies here because the
 * boundary is the visual information that identifies the input. That threshold
 * is the reason for the specific value — it is not a taste pick, and lightening
 * it drops the field back below the line (`border.strong` #D4D4D8 is 1.48:1,
 * `primary[300]` #B0B0B0 is 2.17:1; neither qualifies).
 *
 * ## Why it moved from #8E8E93, and what the extra 0.27 is FOR
 *
 * #8E8E93 was 3.26:1 on white: over the line, by 0.26. That margin is the entire
 * budget any decorative ground may spend before the field boundary fails, and it
 * turned out not to be enough for one — the auth pages' `AuthBackdrop` gradient
 * spent most of it just by rendering its composition correctly, leaving 3.04:1
 * and no room at all for the design to grow.
 *
 * A border's minimum permissible ground luminance is 3·(L_border + 0.05) − 0.05:
 *
 * | border  | L_border | needs ground L ≥ |
 * |---------|----------|------------------|
 * | #8E8E93 | 0.2720   | 0.9161           |
 * | #86868B | 0.2398   | 0.8195           |
 *
 * So this one step buys roughly 0.10 of ground luminance — the difference between
 * a ground that must stay within a whisker of white and one that can carry a
 * visible gradient. It is a MONOTONE improvement: a darker border is higher
 * contrast on every surface, so no page can be made worse by it.
 *
 * Do not lighten it back without re-measuring `AuthBackdrop`'s composited ground
 * at 375×812 AND 1440×900; that surface is now sized against this value.
 */
export const FIELD_BORDER_REST = '#86868B'


/** Retained for callers that want the app's hairline (dividers, not fields). */
export const FIELD_HAIRLINE = borderToken.subtle

/**
 * VERA-291 — design-system consolidation. Single source of truth for the
 * height every text-like field / field-trigger row shares.
 *
 * The value (40) is `components/UI/Input.tsx`'s original height — the
 * "notification drawer's input" (e.g. `EditEmailDrawer`'s email field) that
 * every other field-shaped control (`PhoneField`, `FieldRow`'s read-only
 * trigger, `SelectDrawer`'s full trigger) previously hardcoded its OWN
 * height for (44), so Profile's rows stood visibly taller than the drawers'
 * inputs. `Input.tsx` itself now imports this constant too, so changing the
 * value moves every consumer together. Import this instead of re-hardcoding
 * a number.
 */
export const FIELD_HEIGHT = 40

/**
 * Upper bound on a FIELD's corner radius, independent of the Studio token.
 *
 * Buttons take the brand `border_radius` verbatim; fields take it clamped. The
 * reason is geometric, not aesthetic: **radius does not scale with width.** 10px
 * on a 44px-tall button is proportionally quite round, while the same 10px on a
 * 343px-wide field is barely perceptible — so one token produces two very
 * different visual weights, and matching the numbers does not actually match the
 * appearance. Push the token higher and the divergence gets worse: a button reads
 * as intentionally pill-like where a wide, short field just reads as a lozenge,
 * which fights the "type here" affordance a rectangle gives you.
 *
 * Lowered 10 -> 8 -> 6. Radius does not scale with width, so a value that
 * flatters a 44px button keeps reading as a lozenge on a 343px field. 6 is
 * soft enough not to look punched out of the page and square enough to say
 * "type here" rather than "tap me".
 *
 * Clamped rather than fixed, deliberately. Ignoring the token would break the
 * brand at the other extreme: a brand that sets 0 wants SQUARE, and giving them
 * square buttons beside 8px-rounded fields recreates exactly the mismatch they
 * were avoiding. `min()` honours 0, honours anything modest, and only intervenes
 * where the value would deform the field.
 */
export const FIELD_RADIUS_MAX = 6

/** Field radius for a given theme radius — see FIELD_RADIUS_MAX. */
export const getFieldRadius = (themeRadius: unknown): number => {
  const n = Number(themeRadius)
  return Number.isFinite(n) ? Math.min(n, FIELD_RADIUS_MAX) : FIELD_RADIUS_MAX
}

/**
 * The one field surface: same border, same radius, same fill.
 *
 * Every field-shaped control that is NOT the shared `Input` — the search
 * trigger, the date trigger, the NFC button beside them — used to hand-roll
 * these three properties, and they drifted: the mobile search box shipped with
 * NO border at all while the NFC button beside it and the date field below it
 * both had one, so one row of the filter drawer showed two different field
 * treatments. They also hardcoded `borderRadius: '12px'`, which bypassed the
 * clamp above entirely — the clamp existed but only `Input` ever called it.
 *
 * Spread this instead of re-declaring them. The radius comes from the tenant's
 * Studio token, clamped; the border is the token grey with `!important`,
 * without which index.css's `#vera-shadow-root *` rule wins on ID specificity.
 */
export const fieldSurfaceSx = (theme: any) => ({
  borderRadius: `${getFieldRadius(theme?.shape?.borderRadius)}px`,
  border: `1px solid ${fieldBorderRest(theme)} !important`,
  backgroundColor: theme?.palette?.grey?.[50],
})

/**
 * The ACCENT a field's chrome reacts with — the tenant's own colour.
 *
 * `brand[*]` is derived from the tenant's `primary_color` by
 * `settings/mui/brandRamp.ts`, so in THIS app `brand[600]` means "the brand this
 * UI represents" and degrades to black on an unthemed Vera surface. That is what
 * makes it safe to paint a focus border with: a white-label app must never focus
 * -ring in Vera purple inside a customer's own site, which is the exact bug
 * `brandRamp.ts` was written to remove.
 *
 * The `|| primary.main` arm is a guard for the outer static theme, not a
 * fallback chain that happens to work — both resolve to the same colour by
 * construction (`settings/theme.ts` builds its ramp from the same black).
 */
/**
 * A control's RESTING border — the tenant's Studio "Border color", falling back
 * to `FIELD_BORDER_REST` when there is no theme.
 *
 * ## Why this is the tenant's colour and not the fixed grey
 *
 * `palette.secondary.main` IS Studio's "Border color" (`palette.ts`), and it
 * already wins on every other bordered surface in the app — dividers,
 * `BlockWithBorder`, `ScanHistory`, `TransferOwnershipInfo` — each with the
 * `!important` that beats `index.css`'s ID-specificity blanket. Buttons and
 * fields were the only chrome pinned to a literal, so a brand that set the token
 * saw it everywhere EXCEPT the controls, which is the one place "border colour"
 * most obviously means.
 *
 * ## The cost, stated rather than discovered later
 *
 * `FIELD_BORDER_REST` exists for WCAG 2.2 1.4.11 (3:1 for non-text UI): the
 * default `#E4E4E7` measures 1.27:1 on the page, so a border alone cannot be
 * what identifies a control. That argument is sound for a BARE text input and
 * weak for the controls it was applied to:
 *
 * - A BUTTON carries a visible label or glyph. 1.4.11 asks for 3:1 on the
 *   information REQUIRED to identify the component; the word "Filter" does that,
 *   and the outline is decoration. Applying a field-grade boundary to it was the
 *   blanket rule overreaching, not the criterion binding.
 * - The SEARCH field carries a magnifier glyph and a "Search" placeholder, so it
 *   is identified too — more weakly, and this is the real trade. Its FOCUS state
 *   is unchanged and is where 2.4.11/2.4.13 bind: the accent plus the 6% ring,
 *   black at 21:1 on the unthemed theme.
 *
 * So the resting boundary becomes the tenant's to set, which is what a theming
 * surface called "Border color" promises. A brand that wants a heavier control
 * outline sets a heavier value and gets it everywhere at once.
 */
export const fieldBorderRest = (theme: any): string =>
  theme?.palette?.secondary?.main || FIELD_BORDER_REST

export const fieldAccent = (theme: any): string =>
  theme?.palette?.brand?.[600] || theme?.palette?.primary?.main || '#000000'

/** The soft ring behind a focused border. 6% of the accent, per §31. */
export const fieldAccentRing = (theme: any): string =>
  theme?.palette?.brand?.['600-6%'] || 'rgba(0, 0, 0, 0.06)'

/**
 * A field's border on hover and focus.
 *
 * ## The ladder, and why hover/focus are the ACCENT
 *
 * rest `FIELD_BORDER_REST` #86868B (3.53:1) -> hover the accent -> focus the
 * accent PLUS the 6% ring. This is working-conventions §31's ladder, adapted:
 * §31's rest is the platform's `primary[100]`, which in this app measures
 * 1.24:1 and would leave the field exactly as invisible as it was, so the rest
 * step is this app's own (see `FIELD_BORDER_REST`) while hover/focus follow §31.
 *
 * Focus is the accent rather than another grey because the accent is the only
 * value guaranteed to differ from the neutral rest border at every tenant, and
 * on the unthemed Vera theme it is black — 21:1 against the page, against
 * 3.53:1 at rest. That state change is the focus indicator, and it is what
 * satisfies WCAG 2.2 1.4.11; the ring is reinforcement, not the signal.
 *
 * ## The ring is BACK, deliberately — read this before removing it again
 *
 * An earlier pass deleted the focus halo, on the grounds that it had been
 * hand-rolled four different ways (`ToolbarSearchField` and `CertificatesMenu`
 * as a grey `0 0 0 3px rgba(0,0,0,0.06)`, and `@repo/ui`'s `Input` as a purple
 * one inherited by `SearchFilterSortBar` and `RequestAccessInfo`), so one screen
 * could show two different halos. That objection was about INCONSISTENCY, and it
 * is answered by there being exactly one ring defined in exactly one place —
 * here — not by having no ring at all. Restoring it costs nothing and adds a
 * second, redundant focus cue, which is the right side to err on.
 *
 * `!important`, INSIDE THE SHORTHAND, is load-bearing — not defensive habit.
 * `index.css` applies `#vera-shadow-root * { border-color: … }`, which at ID
 * specificity (1-0-0) beats every emotion `sx` class (0-1-0 / 0-2-0). MEASURED
 * on the certificates toolbar before this change: the field's authored
 * `'&:hover': { borderColor: '#D4D4D8' }` and
 * `'&.Mui-focused': { borderColor: '#A1A1AA' }` BOTH computed to
 * `rgb(228,228,231)` — the rest colour — in every state. Neither had ever
 * rendered. The halo was doing the entire job of showing focus, which is why
 * deleting it without this shorthand would have left the field with no focus
 * indication at all rather than the quieter one that was asked for. A bare
 * `borderColor` here is not a weaker fix, it is a no-op.
 *
 * `boxShadow: 'none'` is stated rather than merely omitted because two of the
 * four consumers render `@repo/ui`'s `Input`, whose own
 * `&.Mui-focused, &:focus-within` sets the brand ring (`Input.tsx`). Leaving the
 * property out would inherit that ring instead of replacing it.
 *
 * Spread it into a field's `sx` AFTER the rest chrome.
 */
/**
 * The MENU-ROW ladder, not the field one — `CertificatesMenu` uses these for a
 * keyboard-only cue on a row whose rest border is the `border.subtle` hairline.
 * A field's ladder is `fieldFocusSx` below and starts from a much darker rest,
 * so these two are deliberately different values for deliberately different
 * chrome. Do not point a field at them.
 */
export const FIELD_BORDER_HOVER = '#D4D4D8'
export const FIELD_BORDER_FOCUS = '#A1A1AA'

/**
 * ⚠️ A FUNCTION of the theme, because the accent is per-tenant. It was a plain
 * object; every call site must now spread `fieldFocusSx(theme)`.
 */
/**
 * How much of the accent a HOVER border shows. Focus shows all of it.
 *
 * Hover and focus used to be the SAME border, separated only by the ring — so on
 * the unthemed theme, where the accent is black, pointing at a field slammed its
 * outline from a near-invisible rest grey to 21:1. Two faults in one: the jump
 * read as intense out of all proportion to "the pointer is over this", and a
 * KEYBOARD focus looked all but identical to a mouse hover, which is the state
 * that actually has to be unmistakable (2.4.11/2.4.13).
 *
 * 0.45 of black on white composites to ~#8C8C8C, 3.36:1 — within rounding of the
 * #86868B this file used to paint at REST. That is the nicest part of the value:
 * the grey that was too heavy to sit on every field all the time is exactly right
 * on the one field a pointer is touching. Expressed as alpha rather than a grey
 * literal so a tenant with a coloured accent gets a soft version of THEIR colour,
 * not a grey that belongs to nobody.
 */
const FIELD_HOVER_ACCENT_ALPHA = 0.45

export const fieldFocusSx = (theme: any) => ({
  '&:hover': {
    border: `1px solid ${alpha(fieldAccent(theme), FIELD_HOVER_ACCENT_ALPHA)} !important`,
    boxShadow: 'none',
  },
  '&.Mui-focused, &:focus-within': {
    border: `1px solid ${fieldAccent(theme)} !important`,
    boxShadow: `0 0 0 3px ${fieldAccentRing(theme)}`,
  },
})

/**
 * The toolbar CONTROL surface — a filter / sort / layout button beside a search
 * field.
 *
 * Extracted from `HeaderBar` on its second use (working-conventions §36): the
 * Activity feed's Filter button was hand-rolled at `size="medium"` with an 18px
 * glyph and its own padding, so the same control rendered at a different height,
 * a different radius and a different icon size on two surfaces of one app.
 *
 * Pair it with `TOOLBAR_ICON_SIZE` — the glyph is half the mismatch, and a
 * shared `sx` cannot carry it.
 */
export const toolbarControlSx = (theme?: any) => ({
  // 44 on a phone, 48 on desktop. 44 is Apple's minimum tap target, NOT a number
  // picked for looks.
  height: { xs: 44, md: 48 },
  minWidth: { xs: 44, md: 48 },
  padding: '12px',
  // Square on mobile (icon only), label-width on desktop.
  paddingX: { xs: '12px', md: '16px' },
  /*
   * `!important` is required here specifically: `createMuiTheme` gives MuiButton
   * a `defaultProps.style` carrying the brand radius, and an INLINE style beats
   * any `sx` rule. Measured — these rendered at the tenant's 10px until this was
   * added. These controls are field-adjacent, not brand buttons, so the clamped
   * field radius is the right family for them.
   */
  borderRadius: `${FIELD_RADIUS_MAX}px !important`,
  border: `1px solid ${fieldBorderRest(theme)} !important`,
  background: '#FFFFFF',
  color: 'text.primary',
  /*
   * Never give way to the search field beside it. A toolbar control has a fixed
   * job and a fixed size; the FIELD is what should absorb the row's slack, which
   * is why every such field carries `flexGrow: 1` and `minWidth: 0`.
   *
   * Here rather than at each call site: two of the three toolbars got it by
   * wrapping their controls in a `flexShrink: 0` Stack, and the third (the
   * Activity feed, whose Filter is a direct child of the row) set it inline on
   * the button. That is the same rule expressed three ways, so one of them was
   * always going to be forgotten by the next caller.
   */
  flexShrink: 0,
  '&:hover': {
    background: '#FAFAFA',
    border: '1px solid #D4D4D8 !important',
  },
}) as const

/**
 * The same control, sized to share a row with the 48px SEARCH FIELD rather than
 * to sit in a row of other buttons.
 *
 * TWO BANDS, and which one applies is decided by the control's NEIGHBOUR, not by
 * what kind of control it is — the same per-band rule working-conventions §29
 * settles for the platform (page header 40, table toolbar 49, fields 49):
 *
 * | band | height | where |
 * |---|---|---|
 * | button row | `{ xs: 44, md: 48 }` | certificates `HeaderBar`'s Filter / Sort / Layout, which sit in the TITLE's row |
 * | field row | 48 at every width | anything abreast of a `ToolbarSearchField` — `HeaderBar`'s Select, the Activity feed's Filter |
 *
 * A height difference is only visible BETWEEN NEIGHBOURS, so 44 is fine in a row
 * of 44s and wrong beside a 48px field. Both stay at or above Apple's 44px tap
 * minimum, so nothing is traded for it.
 *
 * This exists as a named constant rather than each call site writing
 * `{ ...toolbarControlSx, height: 48, minWidth: 48 }` because that spread had
 * already been copied twice and is exactly the drift `toolbarControlSx` was
 * extracted to end. `ToolbarControl` exposes it as a `fieldRow` BOOLEAN rather
 * than an `sx` passthrough — deliberately, since that file refuses to
 * parameterise geometry, and a boolean naming one of two real bands is not the
 * open-ended size prop it is refusing.
 */
export const toolbarControlFieldRowSx = (theme?: any) =>
  ({
    ...toolbarControlSx(theme),
    height: 48,
    minWidth: 48,
  }) as const

/** Glyph size inside a `toolbarControlSx` button. */
export const TOOLBAR_ICON_SIZE = 22
